// Contracts — kids TELEGRAM planning bot (task #39, 2026-07-01).
//
// A Telegram bot (embedded in weern-kids-backend, NOT a separate service) polls
// each moderator for the number of shoots they plan today / this week, and
// writes the answer into mod_plans (the same rows the dashboard reads). Three
// moving parts:
//   1. webhook   POST /api/kids/tg/webhook — receives Telegram updates (public,
//                no Bearer; auth is the shared-secret path param, see below).
//   2. cron      hourly scheduled() — at 08:00 office-local sends the daily poll;
//                Saturday 08:00 the weekly poll.
//   3. tgSend    outbound sendMessage via env.TELEGRAM_BOT_TOKEN.
//
// Only the webhook has a request boundary worth a schema. Telegram's update
// object is large and versioned; we parse ONLY the two shapes we act on
// (message.text + message.chat.id) with a permissive schema — unknown fields are
// ignored (passthrough), and a malformed body is a soft no-op (Telegram retries
// are undesirable, so the webhook always answers 200; see the handler).
import { z } from 'zod';

// Minimal slice of a Telegram Update we consume: a text message with a chat id.
// Everything else (edited_message, callback_query, entities, from, …) is ignored
// — .passthrough() keeps unknown keys from failing the parse. Numbers: Telegram
// chat ids are 64-bit ints; we store the id as TEXT so we never lose precision.
export const TgWebhookUpdateSchema = z
  .object({
    update_id: z.number().optional(),
    message: z
      .object({
        message_id: z.number().optional(),
        text: z.string().max(4096).optional(),
        // Sender — used to auto-bind a moderator by their Telegram @username
        // (matched against users.tg_login collected at registration). username is
        // absent for users who never set a public @handle → we fall back to PID.
        from: z
          .object({
            id: z.number().optional(),
            username: z.string().max(64).optional(),
          })
          .passthrough()
          .optional(),
        chat: z
          .object({
            id: z.number(),
            type: z.string().optional(),
          })
          .passthrough(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough();
export type TgWebhookUpdate = z.infer<typeof TgWebhookUpdateSchema>;
